import { inject, injectable } from 'inversify';
import { NativeMessages } from 'src/native-messages';
import type { NativeBridge } from '../utilities/native-bridge.utility';
import { TYPES } from '../di/types';
import {
  AnalyticsOptions,
  AnalyticsPayload,
  AnalyticsType,
  AnalyticsService,
  AnalyticsPageviewOptions,
} from './types';

@injectable()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class NativeAnalyticsService implements AnalyticsService {
  private currentPageName: string;
  private currentPageOptions: AnalyticsPageviewOptions;

  constructor(
    @inject(TYPES.NativeBridge) private readonly nativeBridge: NativeBridge,
    @inject(TYPES.AppConfig) private readonly appConfig: AppConfig,
  ) {
    this.currentPageName = '';
    this.currentPageOptions = {};
  }

  public logAnalytics(
    options: AnalyticsOptions,
    payload: AnalyticsPayload,
  ): void {
    let nativeMessage: string;

    if (options.isPageview) {
      nativeMessage = NativeMessages.ANALYTICS_PAGEVIEW;
    } else if (options.type === AnalyticsType.CLICKSTREAM_EXPERIMENT) {
      nativeMessage = NativeMessages.CLICKSTREAM_EXPERIMENT;
    } else if (options.type === AnalyticsType.GOOGLE) {
      nativeMessage = payload.nonInteraction
        ? NativeMessages.ANALYTICS_NONINTERACTION
        : NativeMessages.ANALYTICS_ACTION;
    } else {
      // Yes, if there are impressions it is not an "impression clicked" event.
      //
      nativeMessage = payload.impressions
        ? NativeMessages.CLICKSTREAM_MODULE_VISIBLE
        : NativeMessages.CLICKSTREAM_IMPRESSION_CLICKED;
    }

    this.nativeBridge.toNative(nativeMessage, payload);
  }

  public refirePageview(): void {
    this.sendPageview(
      this.currentPageName,
      AnalyticsType.ALL,
      this.currentPageOptions,
    );
  }

  public logPageview(
    pageName: string,
    analyticsType: AnalyticsType = AnalyticsType.ALL,
    options: AnalyticsPageviewOptions = {},
  ): void {
    if (pageName.toLowerCase() !== this.currentPageName.toLowerCase()) {
      this.sendPageview(pageName, analyticsType, options);
      this.setCurrentPageInfo(pageName, options);
    }
  }

  private setCurrentPageInfo(
    pageName: string,
    options?: AnalyticsPageviewOptions,
  ): void {
    this.currentPageName = pageName;
    this.currentPageOptions = options;
  }

  private sendPageview(
    pageName: string,
    analyticsType: AnalyticsType = AnalyticsType.ALL,
    options: AnalyticsPageviewOptions = {},
  ): void {
    const {
      pageGroup = 'convenience features',
      subGroup = 'help',
      vars = {},
      dataLayer = null,
    } = options;
    const dataLayerPayload = dataLayer ? { dataLayer } : {};

    this.logAnalytics(
      {
        type: analyticsType,
        isPageview: true,
      },
      {
        screen_name: pageName,
        page_group: pageGroup,
        sub_group: subGroup,
        brand: this.appConfig.brand,
        clickstreamData: {
          pageName,
          vars,
        },
        ...dataLayerPayload,
      },
    );
  }
}
